import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  BehaviorSubject,
  Subject,
  catchError,
  debounceTime,
  finalize,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import {
  AgentTransactionService,
  AgentTransactionFilters,
  AgentTransactionItemDto,
  AgentTransactionsSummaryDto,
} from '../../../services/agent-transaction.service';
import { ExternalDataService, Agent } from '../../../services/externalDataService.service';
import { PaginationComponent } from '../../shared/pagination/pagination.component';

@Component({
  selector: 'app-agent-transactions-list',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, PaginationComponent],
  templateUrl: './agent-transactions-list.component.html',
  styleUrl: './agent-transactions-list.component.css',
})
export class AgentTransactionsListComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private queryState$ = new BehaviorSubject<AgentTransactionFilters>({
    pageIndex: 0,
    pageSize: 10,
  });

  filterForm: FormGroup;
  agents: Agent[] = [];
  transactions: AgentTransactionItemDto[] = [];
  totalAmountSum = 0;
  totalCount = 0;
  pageIndex = 0;
  pageSize = 10;
  loading = false;
  errorMessage = '';

  readonly transactionTypes = [
    { value: '', label: 'All Types' },
    { value: 'PlatformCharge', label: 'Platform Charge' },
    { value: 'LeadGenerationFee', label: 'Lead Generation Fee' },
    { value: 'VoucherSettlement', label: 'Voucher Settlement' },
    { value: 'SubscriptionSettlement', label: 'Subscription Settlement' },
  ];

  Math = Math;

  constructor(
    private fb: FormBuilder,
    private agentTransactionService: AgentTransactionService,
    private externalDataService: ExternalDataService
  ) {
    this.filterForm = this.fb.group({
      agentId: [''],
      fromDate: [''],
      toDate: [''],
      type: [''],
    });
  }

  ngOnInit(): void {
    this.loadAgents();
    this.setupDataStream();
    this.setupFilterReactivity();
    this.queryState$.next(this.buildFilters(0, this.pageSize));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get currentPage(): number {
    return this.pageIndex + 1;
  }

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize) || 1;
  }

  onSearch(): void {
    this.resetAndFetch();
  }

  clearFilters(): void {
    this.filterForm.reset({
      agentId: '',
      fromDate: '',
      toDate: '',
      type: '',
    });
    this.resetAndFetch();
  }

  onPageChange(page: number): void {
    this.queryState$.next(this.buildFilters(page - 1, this.pageSize));
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.resetAndFetch();
  }

  hasActiveFilters(): boolean {
    const v = this.filterForm.value;
    return !!(v.agentId || v.fromDate || v.toDate || v.type);
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  formatCurrency(amount: number): string {
    return `₹${(amount ?? 0).toFixed(2)}`;
  }

  getTypeLabel(type: string): string {
    const match = this.transactionTypes.find((t) => t.value === type);
    if (match) return match.label;
    return type.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  private setupDataStream(): void {
    this.queryState$
      .pipe(
        tap(() => {
          this.loading = true;
          this.errorMessage = '';
        }),
        switchMap((filters) =>
          this.agentTransactionService.getTransactions(filters).pipe(
            catchError((error) => {
              console.error('Error loading agent transactions:', error);
              this.errorMessage = 'Failed to load agent transactions. Please try again.';
              return of({
                transactions: [],
                totalCount: 0,
                pageIndex: filters.pageIndex ?? 0,
                pageSize: filters.pageSize ?? 10,
                totalAmountSum: 0,
              } as AgentTransactionsSummaryDto);
            }),
            finalize(() => {
              this.loading = false;
            })
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((summary) => this.applySummary(summary));
  }

  private setupFilterReactivity(): void {
    this.filterForm.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => this.resetAndFetch());
  }

  private resetAndFetch(): void {
    this.pageIndex = 0;
    this.queryState$.next(this.buildFilters(0, this.pageSize));
  }

  private buildFilters(pageIndex: number, pageSize: number): AgentTransactionFilters {
    const { agentId, fromDate, toDate, type } = this.filterForm.value;
    this.pageIndex = pageIndex;
    this.pageSize = pageSize;

    return {
      agentId: agentId || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      type: type || undefined,
      pageIndex,
      pageSize,
    };
  }

  private applySummary(summary: AgentTransactionsSummaryDto): void {
    this.transactions = summary.transactions ?? [];
    this.totalCount = summary.totalCount ?? 0;
    this.pageIndex = summary.pageIndex ?? 0;
    this.pageSize = summary.pageSize ?? this.pageSize;
    this.totalAmountSum = summary.totalAmountSum ?? 0;
  }

  private loadAgents(): void {
    this.externalDataService
      .getAllAgents(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (agents) => {
          this.agents = [...agents].sort((a, b) => a.name.localeCompare(b.name));
        },
        error: (error) => console.error('Error loading agents:', error),
      });
  }
}
